<template>
  <v-row>
    <v-col
      cols="12"
      sm="4"
      md="4"
    >
      <VAutocomplete
        ref="RefNetworkType"
        v-model="data.networkType"
        :label="$t('forms.labels.networkType')"
        :items="displayedNetworkTypes"
        itemText="name"
        itemValue="id"
        :loading="loading"
        :validateAtInitialization="!!data.id"
        outlined
        @change="updateData"
      >
        <template #withInfiniteScroll>
          <div v-intersect="(_, __, isIntersecting) => endIntersect(isIntersecting, 'networkTypes')" />
        </template>
      </VAutocomplete>
    </v-col>
    <v-col
      cols="12"
      sm="4"
      md="4"
    >
      <VAutocomplete
        ref="RefComponentType"
        v-model="data.componentType"
        :label="$t('forms.labels.componentType')"
        :items="displayedComponentTypes"
        itemText="name"
        itemValue="id"
        :loading="loading"
        :validateAtInitialization="!!data.id"
        outlined
        @change="updateData"
      >
        <template #withInfiniteScroll>
          <div v-intersect="(_, __, isIntersecting) => endIntersect(isIntersecting, 'componentTypes')" />
        </template>
      </VAutocomplete>
    </v-col>
    <v-col
      cols="12"
      sm="4"
      md="4"
    >
      <VAutocomplete
        ref="autocompleteRef"
        v-model="data.coupon"
        :label="$t('forms.labels.coupon')"
        :clearable="true"
        :items="displayedCoupons"
        :loading="loading"
        :rules="{ required: true }"
        :validateAtInitialization="!!data.id"
        outlined
        @search="searchCoupons"
        @change="onChange"
      >
        <template #withInfiniteScroll>
          <div v-intersect="(_, __, isIntersecting) => endIntersect(isIntersecting, 'coupons')" />
        </template>
      </VAutocomplete>
    </v-col>
    <v-col
      cols="6"
      md="2"
    >
      <VTextField
        v-model="data.norm.excellent"
        :formatNumber="true"
        outlined
        :label="$t('sites.norms.excellent')"
        color="#F44336"
        :rules="{decimal: 8, sNorms: smallerThanNorms('excellent'), bNorms: biggerThanNorms('excellent')}"
      />
    </v-col>
    <v-col
      cols="6"
      md="2"
    >
      <VTextField
        v-model="data.norm.veryGood"
        :formatNumber="true"
        outlined
        :label="$t('sites.norms.veryGood')"
        color="#FDD835"
        :rules="{decimal: 8, sNorms: smallerThanNorms('veryGood'), bNorms: biggerThanNorms('veryGood')}"
      />
    </v-col>
    <v-col
      cols="6"
      md="2"
    >
      <VTextField
        v-model="data.norm.good"
        :formatNumber="true"
        outlined
        :label="$t('sites.norms.good')"
        color="#4CAF50"
        :rules="{decimal: 8, sNorms: smallerThanNorms('good'), bNorms: biggerThanNorms('good')}"
      />
    </v-col>
    <v-col
      cols="6"
      md="2"
    >
      <VTextField
        v-model="data.norm.fair"
        :formatNumber="true"
        outlined
        :label="$t('sites.norms.fair')"
        color="#4CAF50"
        :rules="{decimal: 8, sNorms: smallerThanNorms('fair'), bNorms: biggerThanNorms('fair')}"
      />
    </v-col>
    <v-col
      cols="6"
      md="2"
    >
      <VTextField
        v-model="data.norm.poor"
        :formatNumber="true"
        outlined
        :label="$t('sites.norms.poor')"
        color="#FDD835"
        :rules="{decimal: 8, sNorms: smallerThanNorms('poor'), bNorms: biggerThanNorms('poor')}"
      />
    </v-col>
    <v-col
      cols="6"
      md="2"
    >
      <VTextField
        v-model="data.norm.severe"
        :formatNumber="true"
        outlined
        :label="$t('sites.norms.severe')"
        color="#F44336"
        :rules="{decimal: 8, sNorms: smallerThanNorms('severe'), bNorms: biggerThanNorms('severe')}"
      />
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex';
import { cloneDeep, isEmpty } from 'lodash';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';

export default {
    components: {
        VTextField,
        VAutocomplete,
    },
    props: {
        standard: {
            type: Object,
            default: () => ({}),
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
        standards: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            normKeys: ['excellent', 'veryGood', 'good', 'fair', 'poor', 'severe'],
            // Backend takes null for 'any'
            networkTypes: [
                {
                    id: null,
                    name: this.$t('forms.labels.any'),
                },
            ],
            componentTypes: [
                {
                    id: null,
                    name: this.$t('forms.labels.any'),
                },
            ],
            coupons: [],
            data: {
                networkType: null,
                componentType: null,
                coupon: null,
                norm: {
                    excellent: null,
                    veryGood: null,
                    good: null,
                    fair: null,
                    poor: null,
                    severe: null,
                },
            },
            loading: false,
            couponsQueryParams: { perPage: 25 },
            couponsPagination: {},
            pagination: {},
            queryParams: { page: 1, perpage: 25, search: '' },
            networksTypesPagination: {},
            componentTypesPagination: {},
            networksTypesQueryParams: { page: 1, perpage: 25, search: '' },
            componentTypesQueryParams: { page: 1, perpage: 25, search: '' },
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'teamMainLang',
        ]),
        displayedCoupons() {
            return this.coupons.map(({ id, name }) => ({
                id,
                name: name[this.userLang] || name[this.teamMainLang],
                disabled: this.isCouponUsed(id),
            }));
        },
        displayedComponentTypes() {
            return this.getDisplayedItem(this.componentTypes);
        },
        displayedNetworkTypes() {
            return this.getDisplayedItem(this.networkTypes);
        },
    },
    watch: {
        data: {
            handler() {
                this.updateData();
            },
            deep: true,
        },
    },
    async created() {
        if (this.isEdition) {
            const clonedStandard = cloneDeep(this.standard);
            const std = this.standards?.find(standard => standard.id === this.standard?.id);
            if (std?.id && std?.norm) {
                this.data = {
                    ...clonedStandard,
                    norm: cloneDeep(std.norm),
                };
            } else {
                this.data = clonedStandard;
            }
        }
        await this.getData();
    },
    methods: {
        async getData() {
            this.loading = true;
            await this.getNetworkTypes();
            await this.getComponentTypes();
            await this.getCoupons();
            this.loading = false;
        },
        async getNetworkTypes() {
            if (this.isEdition) {
                const { data } = await this.$rq.getNetworkTypes({ pagination: false });
                this.networkTypes = data.networkTypes;
            } else if (this.networksTypesPagination?.currentPage !== this.networksTypesQueryParams?.page
            && this.networksTypesPagination?.totalPageCount
                ? this.networksTypesPagination?.totalPageCount >= this.networksTypesQueryParams?.page : true) {
                const { data } = await this.$rq.getNetworkTypes(this.networksTypesQueryParams);
                this.networkTypes = [...data.networkTypes, ...this.networkTypes];
                this.networksTypesPagination = data.pagination;
            }
        },
        async getComponentTypes() {
            if (this.isEdition) {
                const { data } = await this.$rq.getComponentTypes({ pagination: false });
                this.componentTypes = data.componentTypes;
            } else if (this.componentTypesPagination?.currentPage !== this.componentTypesQueryParams?.page
            && this.componentTypesPagination?.totalPageCount
                ? this.componentTypesPagination?.totalPageCount >= this.componentTypesQueryParams?.page : true) {
                const { data } = await this.$rq.getComponentTypes(this.componentTypesQueryParams);
                this.componentTypes = [...data.componentTypes, ...this.componentTypes];
                this.componentTypesPagination = data.pagination;
            }
        },
        async getCoupons() {
            // for edit mode skip the pagination
            if (this.isEdition) {
                const { data: { coupons } } = await this.$rq.getCoupons({ pagination: false });
                this.coupons = coupons;
            } else if (isEmpty(this.couponsPagination) || (this.couponsPagination?.currentPage !== this.couponsQueryParams?.page)) {
                const { data } = await this.$rq.getCoupons(this.couponsQueryParams);
                this.coupons = [...this.coupons, ...data.coupons];
                this.couponsPagination = data.pagination;
            }
        },
        hasType(standardType, dataType) {
            return standardType ? dataType === standardType.id : dataType === null;
        },
        updateData() {
            this.$emit('sendData', this.data);
        },
        isCouponUsed(id) {
            const existingStandard = this.standards.filter(standard => {
                const { componentType, networkType } = standard;
                return this.hasType(componentType, this.data.componentType)
                && this.hasType(networkType, this.data.networkType);
            });
            const selectedCouponTypes = [];
            existingStandard.forEach(standard => {
                if (standard.coupon) {
                    selectedCouponTypes.push(standard.coupon.id);
                }
            });
            return selectedCouponTypes.includes(id);
        },
        smallerThanNorms(normKey) {
            const normIndex = this.normKeys.findIndex(key => key === normKey);
            const normKeysBefore = this.normKeys.slice(0, normIndex);

            return normKeysBefore.map(key => parseFloat(this.data.norm[key]));
        },
        biggerThanNorms(normKey) {
            const normIndex = this.normKeys.findIndex(key => key === normKey);
            const normKeysAfter = this.normKeys.slice(normIndex + 1);

            return normKeysAfter.map(key => parseFloat(this.data.norm[key]));
        },
        async endIntersectCoupons(isIntersecting) {
            const condition = this.couponsPagination.currentPage < this.couponsPagination.totalPageCount;
            if (isIntersecting && condition) {
                this.couponsQueryParams.page = this.couponsPagination.currentPage + 1;
                await this.getCoupons();
                // access "v-autocomplete" ref
                const ref = this.$refs?.autocompleteRef?.$refs?.autocomplete;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async searchCoupons(search) {
            const { data } = await this.$rq.getCoupons({ search, pagination: false });
            this.coupons = data.coupons;
        },
        async onChange(val) {
            // when change is clear
            if (!val) {
                this.couponsPagination = {};
                this.couponsQueryParams = {};
                this.coupons = [];
                await this.getCoupons();
            }
        },
        getDisplayedItem(item) {
            return item?.map(({ id, name }) => ({
                id,
                name: id ? name[this.selectedLang] || name[this.teamMainLang] : name,
            }));
        },
        async endIntersectComponentTypes(isIntersecting) {
            const condition = this.componentTypesPagination?.currentPage < this.componentTypesPagination?.totalPageCount;
            if (isIntersecting && condition) {
                this.componentTypesQueryParams.page = this.componentTypesPagination.currentPage + 1;
                await this.getComponentTypes();
                const ref = this.$refs?.refComponentTypes?.$refs?.autocomplete;
                if (ref?.onScroll) {
                    // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async endIntersectNetworkTypes(isIntersecting) {
            const condition = this.networksTypesPagination?.currentPage < this.networksTypesPagination?.totalPageCount;
            if (isIntersecting && condition) {
                this.networksTypesQueryParams.page = this.networksTypesPagination.currentPage + 1;
                await this.getNetworkTypes();
                const ref = this.$refs?.refNetworkTypes?.$refs?.autocomplete;
                if (ref?.onScroll) {
                    // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async endIntersect(isIntersecting, field) {
            switch (field) {
                case 'componentTypes':
                    await this.endIntersectComponentTypes(isIntersecting);
                    break;
                case 'networkTypes':
                    await this.endIntersectNetworkTypes(isIntersecting);
                    break;
                case 'coupons':
                    await this.endIntersectCoupons(isIntersecting);
                    break;
                default:
            }
        },
    },
};
</script>
